import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2,
  ExternalLink,
  Eye,
  FileCheck2,
  FilePen,
  FileSignature,
  FileText,
  GitBranch,
  History,
  Link as LinkIcon,
  MoreVertical,
  Plus,
  Printer,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react'
import { type ChangeEvent, useMemo, useRef, useState } from 'react'
import { ContractEditorDrawer } from '../components/ContractEditorDrawer'
import { DPAEditorDrawer } from '../components/DPAEditorDrawer'
import { ProposalEditorDrawer } from '../components/ProposalEditorDrawer'
import { ProposalHistoryDialog } from '../components/ProposalHistoryDialog'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge, type Variant } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { useConfirm } from '../components/ui/ConfirmDialog'
import { Dialog } from '../components/ui/Dialog'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label, Select } from '../components/ui/Input'
import { Menu, MenuDivider, MenuItem } from '../components/ui/Menu'
import { apiClient } from '../lib/api'
import { formatDate } from '../lib/utils'

type ProposalRow = {
  id: string
  code: string
  year: number
  number: number
  revision: number
  type: 'PRV' | 'CTR' | 'DPA'
  clientId: string | null
  clientName: string | null
  leadId: string | null
  projectId: string | null
  title: string
  subtitle: string | null
  status: string
  totalAmountEur: string | null
  vatRate: string
  validUntil: string | null
  sentAt: string | null
  acceptedAt: string | null
  updatedAt: string
  signedDocumentObjectKey: string | null
  signedDocumentUploadedAt: string | null
  signedDocumentContentType: string | null
  signedDocumentSize: number | null
  signedDocumentOriginalFilename: string | null
  signedDocumentUploadedVia: 'admin' | 'public_link' | null
  signedDocumentUploaderIp: string | null
  signedDocumentUploaderUserAgent: string | null
  publicLinkToken: string | null
  publicLinkExpiresAt: string | null
  publicLinkUsedAt: string | null
  rejectionReason: string | null
  rejectionVia: 'admin' | 'public_link' | null
  rejectionIp: string | null
  rejectionUserAgent: string | null
  rejectedAt: string | null
  /**
   * Metadata stored at upsert time. Two cross-link conventions co-exist:
   *
   *  - Legacy single-string: `linkedPrv` / `linkedCtr` (1:1 PRV ↔ CTR). Still
   *    populated on older records and on manual mergeMetadata calls.
   *  - New generalized: `related[]` with shape
   *    { type:'PRV'|'CTR'|'DPA', code, role:'parent'|'allegato-b'|'related-to'|... }.
   *    Written by the DPA factory (bilateral patch) and by future factories
   *    that need 1:N cross-references.
   *
   * The list view merges both into a single set of clickable badges.
   */
  metadata: {
    linkedPrv?: string
    linkedCtr?: string
    related?: Array<{ type: 'PRV' | 'CTR' | 'DPA'; code: string; role?: string; note?: string }>
    [key: string]: unknown
  } | null
}

type ProposalTab = 'PRV' | 'CTR' | 'DPA' | 'all'

type ListResult = { results: ProposalRow[]; total: number; limit: number; offset: number }
type Client = { id: string; name: string }

const statusVariantMap: Record<string, Variant> = {
  draft: 'muted',
  sent: 'info',
  pending_review: 'warning',
  accepted: 'success',
  rejected: 'danger',
  expired: 'warning',
  superseded: 'muted',
}

const statusLabel: Record<string, string> = {
  draft: 'Bozza',
  sent: 'Inviata',
  pending_review: 'In Attesa di Verifica',
  accepted: 'Accettata',
  rejected: 'Rifiutata',
  expired: 'Scaduta',
  superseded: 'Superata',
}

export function ProposalsPage() {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [activeTab, setActiveTab] = useState<ProposalTab>('PRV')
  const [clientFilter, setClientFilter] = useState<string>('')
  const [includeSuperseded, setIncludeSuperseded] = useState(false)
  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [editing, setEditing] = useState<{ id: string; type: 'PRV' | 'CTR' | 'DPA' } | null>(null)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [uploadingSignedFor, setUploadingSignedFor] = useState<ProposalRow | null>(null)
  const [reviewingSignedFor, setReviewingSignedFor] = useState<ProposalRow | null>(null)
  const [linkInfoFor, setLinkInfoFor] = useState<ProposalRow | null>(null)
  const [rejectionInfoFor, setRejectionInfoFor] = useState<ProposalRow | null>(null)
  const [rejectingProposalFor, setRejectingProposalFor] = useState<ProposalRow | null>(null)
  const [reviseId, setReviseId] = useState<string | null>(null)
  const [historyOf, setHistoryOf] = useState<{ id: string; revision: number } | null>(null)

  // We fetch ALL proposals (no type filter on the server) so we can build the
  // PRV ↔ CTR cross-link map in-memory and switch tabs instantly without an
  // extra round-trip. limit=200 is the API cap; well above current volume.
  const params = new URLSearchParams()
  params.set('limit', '200')
  if (statusFilter) params.set('status', statusFilter)
  if (clientFilter) params.set('clientId', clientFilter)
  if (includeSuperseded) params.set('includeSuperseded', 'true')
  const qs = params.toString()

  const proposalsQ = useQuery({
    queryKey: ['proposals', { statusFilter, clientFilter, includeSuperseded }],
    queryFn: () => apiClient.get<ListResult>(`/proposals?${qs}`),
  })

  const clientsQ = useQuery({
    queryKey: ['clients', { all: true }],
    queryFn: () => apiClient.get<{ results: Client[] }>('/clients?limit=200'),
  })

  /**
   * Map of `code → row` across BOTH types, so we can resolve cross-references
   * (CTR.metadata.linkedPrv → the actual PRV row) without an extra fetch.
   * Built from the full result set, before tab/search filtering.
   */
  const byCode = useMemo(() => {
    const m = new Map<string, ProposalRow>()
    for (const r of proposalsQ.data?.results ?? []) m.set(r.code, r)
    return m
  }, [proposalsQ.data])

  /** Strip the `-RN` revision suffix from a stored code, if present. */
  const stripRev = (code: string): string => code.replace(/-R\d+$/i, '')

  /**
   * Resolve all linked counterpart rows for a proposal. Merges the legacy
   * single-string cross-link (linkedPrv / linkedCtr) with the new
   * metadata.related[] array. De-dups by code so a document that's linked
   * via BOTH conventions appears only once.
   */
  const linkedRowsFor = (p: ProposalRow): ProposalRow[] => {
    const codes = new Set<string>()
    const legacyCode = p.type === 'PRV' ? p.metadata?.linkedCtr : p.metadata?.linkedPrv
    if (legacyCode) codes.add(stripRev(legacyCode))
    for (const rel of p.metadata?.related ?? []) {
      if (rel.code) codes.add(stripRev(rel.code))
    }
    const rows: ProposalRow[] = []
    for (const c of codes) {
      const row = byCode.get(c)
      if (row) rows.push(row)
    }
    return rows
  }

  /** Counts per tab — driven off the unfiltered server result. */
  const tabCounts = useMemo(() => {
    const all = proposalsQ.data?.results ?? []
    return {
      PRV: all.filter((r) => r.type === 'PRV').length,
      CTR: all.filter((r) => r.type === 'CTR').length,
      DPA: all.filter((r) => r.type === 'DPA').length,
      all: all.length,
    }
  }, [proposalsQ.data])

  const filtered = useMemo(() => {
    const rows = proposalsQ.data?.results ?? []
    const byType = activeTab === 'all' ? rows : rows.filter((r) => r.type === activeTab)
    if (!search.trim()) return byType
    const q = search.trim().toLowerCase()
    return byType.filter(
      (r) =>
        r.code.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        (r.subtitle ?? '').toLowerCase().includes(q) ||
        (r.clientName ?? '').toLowerCase().includes(q),
    )
  }, [proposalsQ.data, search, activeTab])

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.del(`/proposals/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proposals'] }),
  })

  async function onDelete(p: ProposalRow) {
    const ok = await confirm({
      title: `Eliminare ${p.code} R${p.revision}?`,
      message:
        'Il record verrà soft-deleted: rimane nel database per audit ma non sarà più visibile nelle liste. Per riattivarlo serve un intervento manuale.',
      confirmLabel: 'Elimina',
      tone: 'danger',
    })
    if (!ok) return
    deleteMut.mutate(p.id)
  }

  return (
    <AppLayout>
      <PageHeader
        title="Proposte e Contratti"
        description="PRV (Proposta Tecnico-Commerciale) e CTR (Contratto di Fornitura)"
        action={
          <Button onClick={() => setNewDialogOpen(true)}>
            <Plus className="size-4" /> Nuova proposta
          </Button>
        }
      />

      {/* Type tabs — replace the old "Tipo" select. The active tab controls
          which rows are visible; counts come from the full unfiltered set. */}
      <div className="mb-4 flex gap-1 border-b border-zinc-800/70">
        {(
          [
            { id: 'PRV', label: 'Proposte', count: tabCounts.PRV },
            { id: 'CTR', label: 'Contratti', count: tabCounts.CTR },
            { id: 'DPA', label: 'DPA', count: tabCounts.DPA },
            { id: 'all', label: 'Tutti', count: tabCounts.all },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px flex items-center gap-2 ${
              activeTab === t.id
                ? 'text-cyan-300 border-cyan-400'
                : 'text-zinc-500 border-transparent hover:text-zinc-200'
            }`}
          >
            {t.label}
            <span
              className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                activeTab === t.id ? 'bg-cyan-500/15 text-cyan-200' : 'bg-zinc-800/60 text-zinc-500'
              }`}
            >
              {t.count}
            </span>
          </button>
        ))}
      </div>

      <Card className="mb-4 p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-5">
            <Label htmlFor="search-proposals">Cerca</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500 pointer-events-none" />
              <Input
                id="search-proposals"
                placeholder="Codice, titolo, cliente…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <Label htmlFor="filter-status">Stato</Label>
            <Select
              id="filter-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.currentTarget.value)}
            >
              <option value="">Tutti</option>
              <option value="draft">Bozza</option>
              <option value="sent">Inviata</option>
              <option value="accepted">Accettata</option>
              <option value="rejected">Rifiutata</option>
              <option value="expired">Scaduta</option>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label htmlFor="filter-client">Cliente</Label>
            <Select
              id="filter-client"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.currentTarget.value)}
            >
              <option value="">Tutti</option>
              {clientsQ.data?.results.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="md:col-span-1 flex items-end">
            <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
              <input
                type="checkbox"
                checked={includeSuperseded}
                onChange={(e) => setIncludeSuperseded(e.target.checked)}
                className="accent-cyan-500"
              />
              Rev. vecchie
            </label>
          </div>
        </div>
      </Card>

      {proposalsQ.isLoading ? (
        <Card className="p-6">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-6">
          <Empty
            icon={FileText}
            title="Nessuna proposta"
            description="Crea una nuova PRV per iniziare. Le proposte accettate generano automaticamente Progetto e Fattura bozza."
            action={
              <Button onClick={() => setNewDialogOpen(true)}>
                <Plus className="size-4" /> Nuova proposta
              </Button>
            }
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              {/* Codice/Cliente | Titolo (flex) | Stato | Importo | Azioni */}
              <col style={{ width: '220px' }} />
              <col />
              <col style={{ width: '170px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '150px' }} />
            </colgroup>
            <thead className="bg-zinc-900/60 text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2.5 font-medium whitespace-nowrap">
                  Codice · Cliente
                </th>
                <th className="text-left px-3 py-2.5 font-medium">Titolo</th>
                <th className="text-left px-3 py-2.5 font-medium whitespace-nowrap">Stato</th>
                <th className="text-right px-3 py-2.5 font-medium whitespace-nowrap">Importo</th>
                <th className="text-right px-3 py-2.5 font-medium whitespace-nowrap">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-zinc-800 hover:bg-zinc-900/40 transition">
                  <td className="px-3 py-2.5 align-top">
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <span className="font-mono text-xs text-cyan-300">{p.code}</span>
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                        R{p.revision}
                      </span>
                      <Badge
                        variant={p.type === 'CTR' ? 'brand' : p.type === 'DPA' ? 'success' : 'info'}
                      >
                        {p.type}
                      </Badge>
                      {linkedRowsFor(p).map((linked) => (
                        <button
                          key={linked.id}
                          type="button"
                          onClick={() => setEditing({ id: linked.id, type: linked.type })}
                          title={`Apri ${linked.type} collegato: ${linked.code}`}
                          className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-mono transition ${
                            linked.type === 'CTR'
                              ? 'text-zinc-400 hover:text-cyan-300 hover:bg-cyan-500/10'
                              : linked.type === 'DPA'
                                ? 'text-zinc-400 hover:text-emerald-300 hover:bg-emerald-500/10'
                                : 'text-zinc-400 hover:text-sky-300 hover:bg-sky-500/10'
                          }`}
                        >
                          ↳ {linked.code}
                        </button>
                      ))}
                    </div>
                    <div
                      className="text-xs text-zinc-400 truncate mt-0.5"
                      title={p.clientName ?? undefined}
                    >
                      {p.clientName ?? '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    <div className="text-zinc-100 truncate" title={p.title}>
                      {p.title}
                    </div>
                    {p.subtitle && (
                      <div className="text-xs text-zinc-500 truncate mt-0.5" title={p.subtitle}>
                        {p.subtitle}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-top whitespace-nowrap">
                    {p.status === 'rejected' && p.rejectionReason ? (
                      <button
                        type="button"
                        onClick={() => setRejectionInfoFor(p)}
                        className="inline-flex items-center gap-1 group"
                        title={`Rifiutata${p.rejectionVia === 'public_link' ? ' dal cliente' : ''} — clicca per dettagli`}
                      >
                        <Badge variant={statusVariantMap[p.status] ?? 'default'}>
                          {statusLabel[p.status] ?? p.status}
                        </Badge>
                        <span className="text-[10px] text-zinc-500 group-hover:text-zinc-300 underline decoration-dotted">
                          dettagli
                        </span>
                      </button>
                    ) : (
                      <Badge variant={statusVariantMap[p.status] ?? 'default'}>
                        {statusLabel[p.status] ?? p.status}
                      </Badge>
                    )}
                    <div
                      className="text-[10px] text-zinc-500 mt-1"
                      title={`Aggiornata ${formatDate(p.updatedAt)}`}
                    >
                      agg. {formatDate(p.updatedAt)}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 align-top text-right tabular-nums text-zinc-200 whitespace-nowrap">
                    {p.totalAmountEur
                      ? `€ ${Number(p.totalAmountEur).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '—'}
                  </td>
                  <td className="px-3 py-2.5 align-top whitespace-nowrap">
                    <div className="flex justify-end items-center gap-1">
                      {/* Visible inline: visualizza + modifica */}
                      <IconBtn title="Anteprima" onClick={() => setPreviewId(p.id)}>
                        <Eye className="size-3.5" />
                      </IconBtn>
                      <IconBtn
                        title="Modifica"
                        onClick={() => setEditing({ id: p.id, type: p.type })}
                      >
                        <FileText className="size-3.5" />
                      </IconBtn>

                      {/* Visual hint if a signed document is already attached — keep it
                            outside the menu as a one-click status indicator. */}
                      {p.signedDocumentObjectKey && (
                        <IconBtn
                          title={`Firmato caricato${
                            p.signedDocumentOriginalFilename
                              ? ` — ${p.signedDocumentOriginalFilename}`
                              : ''
                          }`}
                          tone="success"
                          onClick={() => {
                            const url = signedDocUrl(p)
                            if (url) window.open(url, '_blank')
                          }}
                        >
                          <FileCheck2 className="size-3.5" />
                        </IconBtn>
                      )}

                      {/* Hamburger: everything else */}
                      <Menu
                        triggerTitle="Altre azioni"
                        trigger={<MoreVertical className="size-4" />}
                      >
                        {/* Pending review: verifica firmato */}
                        {p.status === 'pending_review' && (
                          <MenuItem
                            icon={ShieldCheck}
                            tone="success"
                            onClick={() => setReviewingSignedFor(p)}
                          >
                            Verifica firmato (approva / rifiuta)
                          </MenuItem>
                        )}

                        {/* Public link controls (when status sent + token exists) */}
                        {p.status === 'sent' && p.publicLinkToken && (
                          <MenuItem icon={LinkIcon} onClick={() => setLinkInfoFor(p)}>
                            Link pubblico di firma
                          </MenuItem>
                        )}

                        {p.status !== 'superseded' &&
                          p.status !== 'rejected' &&
                          p.status !== 'pending_review' &&
                          !p.signedDocumentObjectKey && (
                            <MenuItem
                              icon={FileSignature}
                              tone="success"
                              onClick={() => setUploadingSignedFor(p)}
                            >
                              Carica firmato → Accetta
                            </MenuItem>
                          )}
                        {p.signedDocumentObjectKey && (
                          <MenuItem
                            icon={ExternalLink}
                            onClick={() => {
                              const url = signedDocUrl(p)
                              if (url) window.open(url, '_blank')
                            }}
                          >
                            Apri firmato
                          </MenuItem>
                        )}
                        {p.signedDocumentObjectKey &&
                          p.status !== 'superseded' &&
                          p.status !== 'rejected' &&
                          p.status !== 'pending_review' && (
                            <MenuItem icon={FileSignature} onClick={() => setUploadingSignedFor(p)}>
                              Sostituisci firmato
                            </MenuItem>
                          )}

                        {(p.signedDocumentObjectKey ||
                          p.status === 'sent' ||
                          p.status === 'draft' ||
                          p.status === 'pending_review') && <MenuDivider />}

                        {p.status !== 'superseded' && (
                          <MenuItem icon={FilePen} onClick={() => setReviseId(p.id)}>
                            {p.revision > 0 ? 'Crea nuova revisione' : 'Crea revisione'}
                          </MenuItem>
                        )}
                        {p.revision > 0 && (
                          <MenuItem
                            icon={History}
                            onClick={() => setHistoryOf({ id: p.id, revision: p.revision })}
                          >
                            Storico revisioni
                          </MenuItem>
                        )}

                        {/* Admin reject — available while the proposal is still
                              "open" (draft / sent / pending_review). Marks the row
                              as rejected with a reason; distinct from
                              "reject signed only" which goes back to sent. */}
                        {(p.status === 'draft' ||
                          p.status === 'sent' ||
                          p.status === 'pending_review') && (
                          <MenuItem
                            icon={XCircle}
                            tone="danger"
                            onClick={() => setRejectingProposalFor(p)}
                          >
                            Rifiuta proposta
                          </MenuItem>
                        )}

                        <MenuDivider />

                        <MenuItem icon={Trash2} tone="danger" onClick={() => onDelete(p)}>
                          Elimina
                        </MenuItem>
                      </Menu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {newDialogOpen && (
        <NewProposalDialog
          onClose={() => setNewDialogOpen(false)}
          onCreated={(id, type) => {
            setNewDialogOpen(false)
            setEditing({ id, type })
          }}
        />
      )}

      {editing &&
        (editing.type === 'CTR' ? (
          <ContractEditorDrawer proposalId={editing.id} onClose={() => setEditing(null)} />
        ) : editing.type === 'DPA' ? (
          <DPAEditorDrawer proposalId={editing.id} onClose={() => setEditing(null)} />
        ) : (
          <ProposalEditorDrawer proposalId={editing.id} onClose={() => setEditing(null)} />
        ))}

      {previewId && <PreviewDialog id={previewId} onClose={() => setPreviewId(null)} />}

      {uploadingSignedFor && (
        <UploadSignedDialog
          proposal={uploadingSignedFor}
          onClose={() => setUploadingSignedFor(null)}
        />
      )}

      {reviewingSignedFor && (
        <ReviewSignedDialog
          proposal={reviewingSignedFor}
          onClose={() => setReviewingSignedFor(null)}
        />
      )}

      {linkInfoFor && (
        <PublicLinkDialog proposal={linkInfoFor} onClose={() => setLinkInfoFor(null)} />
      )}

      {rejectionInfoFor && (
        <RejectionInfoDialog
          proposal={rejectionInfoFor}
          onClose={() => setRejectionInfoFor(null)}
        />
      )}

      {rejectingProposalFor && (
        <RejectProposalDialog
          proposal={rejectingProposalFor}
          onClose={() => setRejectingProposalFor(null)}
        />
      )}

      {reviseId && <ReviseDialog id={reviseId} onClose={() => setReviseId(null)} />}

      {historyOf && (
        <ProposalHistoryDialog
          proposalId={historyOf.id}
          currentRevision={historyOf.revision}
          onClose={() => setHistoryOf(null)}
        />
      )}
    </AppLayout>
  )
}

function IconBtn({
  children,
  onClick,
  title,
  tone,
}: {
  children: React.ReactNode
  onClick: () => void
  title: string
  tone?: 'success' | 'danger'
}) {
  const cls =
    tone === 'success'
      ? 'text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300'
      : tone === 'danger'
        ? 'text-pink-400 hover:bg-pink-500/10 hover:text-pink-300'
        : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100'
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`p-1.5 rounded-md transition ${cls}`}
    >
      {children}
    </button>
  )
}

// ============================================================
// New proposal dialog — minimal create
// ============================================================
function NewProposalDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (id: string, type: 'PRV' | 'CTR') => void
}) {
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [type, setType] = useState<'PRV' | 'CTR'>('PRV')
  const [clientId, setClientId] = useState('')
  const [totalAmount, setTotalAmount] = useState('')
  const [validityDays, setValidityDays] = useState('30')

  const clientsQ = useQuery({
    queryKey: ['clients', { all: true }],
    queryFn: () => apiClient.get<{ results: Client[] }>('/clients?limit=200'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient.post<{ created: boolean; proposal: { id: string; type: 'PRV' | 'CTR' } }>(
        '/proposals',
        body,
      ),
    onSuccess: (data) => {
      onCreated(data.proposal.id, data.proposal.type)
    },
  })

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const client = clientsQ.data?.results.find((c) => c.id === clientId)
    create.mutate({
      type,
      title,
      subtitle: subtitle || undefined,
      clientId: clientId || undefined,
      totalAmountEur: totalAmount ? Number(totalAmount) : undefined,
      validityDays: Number(validityDays) || 30,
      content:
        type === 'CTR'
          ? emptyContractContent(client?.name ?? '')
          : emptyContent(client?.name ?? ''),
    })
  }

  return (
    <Dialog open onClose={onClose} title="Nuova proposta" size="md">
      <form onSubmit={onSubmit} className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="new-type">Tipo</Label>
            <Select
              id="new-type"
              value={type}
              onChange={(e) => setType(e.currentTarget.value as 'PRV' | 'CTR')}
            >
              <option value="PRV">PRV — Proposta</option>
              <option value="CTR">CTR — Contratto</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="new-validity">Validità (gg)</Label>
            <Input
              id="new-validity"
              type="number"
              value={validityDays}
              onChange={(e) => setValidityDays(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="new-client">Cliente</Label>
          <Select
            id="new-client"
            value={clientId}
            onChange={(e) => setClientId(e.currentTarget.value)}
          >
            <option value="">— bozza senza cliente —</option>
            {clientsQ.data?.results.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="new-title">Titolo</Label>
          <Input
            id="new-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="Es. Sistema gestione X"
          />
        </div>
        <div>
          <Label htmlFor="new-subtitle">Sottotitolo</Label>
          <Input
            id="new-subtitle"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            placeholder="Breve descrizione del progetto"
          />
        </div>
        <div>
          <Label htmlFor="new-amount">Importo totale (EUR, IVA esclusa)</Label>
          <Input
            id="new-amount"
            type="number"
            step="0.01"
            value={totalAmount}
            onChange={(e) => setTotalAmount(e.target.value)}
            placeholder="0.00"
          />
        </div>
        {create.isError && (
          <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
            Errore: {String(create.error)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" disabled={create.isPending || !title.trim()}>
            {create.isPending ? 'Creazione…' : 'Crea bozza'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

// ============================================================
// Preview dialog — server-rendered HTML in iframe
// ============================================================
function PreviewDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const previewUrl = `/api/proposals/${id}/preview`
  return (
    <Dialog open onClose={onClose} title="Anteprima proposta" size="xl">
      <div className="p-4 flex flex-col" style={{ height: '70vh' }}>
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs text-zinc-500">
            Per esportare in PDF: Ctrl+P sull&apos;anteprima → Stampa &gt; Salva come PDF.
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const w = window.open(previewUrl, '_blank')
              if (w) setTimeout(() => w.print(), 600)
            }}
          >
            <Printer className="size-3.5" /> Apri + stampa
          </Button>
        </div>
        <iframe
          src={previewUrl}
          title="Anteprima"
          className="flex-1 w-full rounded-md border border-zinc-800 bg-white"
        />
      </div>
    </Dialog>
  )
}

// ============================================================
// Upload signed document dialog — replaces the legacy "Accept + cascade"
// dialog. The user uploads the client-signed PDF/scan; the backend stores
// it on MinIO and flips the proposal status to "accepted". Project and
// invoice creation are handled separately by the user.
// ============================================================
function UploadSignedDialog({
  proposal,
  onClose,
}: {
  proposal: ProposalRow
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const upload = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData()
      fd.append('file', f)
      const res = await fetch(`/api/proposals/${proposal.id}/upload-signed`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(`Upload fallito (${res.status}): ${text}`)
      }
      return (await res.json()) as {
        publicUrl: string
        contentType: string
        size: number
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      onClose()
    },
  })

  function pickFile(f: File) {
    setFile(f)
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.[0]) pickFile(e.target.files[0])
    e.target.value = ''
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer.files[0]) pickFile(e.dataTransfer.files[0])
  }

  const docLabel = proposal.type === 'CTR' ? 'contratto' : 'proposta'

  return (
    <Dialog open onClose={onClose} title={`Carica ${docLabel} firmata`} size="md">
      <div className="p-5 space-y-4">
        <p className="text-sm text-zinc-400 leading-relaxed">
          Carica il file <strong>firmato dal cliente</strong> (PDF, JPG, PNG, WebP, HEIC). Lo stato
          di{' '}
          <span className="font-mono text-cyan-300">
            {proposal.code} R{proposal.revision}
          </span>{' '}
          passerà automaticamente a <span className="text-emerald-300">Accettata</span>. Progetto e
          fattura li gestisci separatamente dalle rispettive pagine.
        </p>

        {/* biome-ignore lint/a11y/useSemanticElements: drop zone is a div */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click()
          }}
          onDrop={onDrop}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            setDragging(false)
          }}
          className={`rounded-lg border-2 border-dashed transition-all cursor-pointer p-6 text-center ${
            dragging
              ? 'border-cyan-400 bg-cyan-500/10'
              : file
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : 'border-zinc-700 bg-zinc-950/40 hover:border-cyan-500/50'
          }`}
        >
          {file ? (
            <div className="space-y-1">
              <FileCheck2 className="mx-auto size-7 text-emerald-300" />
              <div className="text-sm text-zinc-100 font-medium truncate">{file.name}</div>
              <div className="text-xs text-zinc-500">
                {formatBytes(file.size)} · {file.type || 'tipo sconosciuto'}
              </div>
              <div className="text-[11px] text-zinc-500 mt-2">
                Clicca per scegliere un file diverso
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <Upload
                className={`mx-auto size-7 ${dragging ? 'text-cyan-300' : 'text-zinc-500'}`}
              />
              <div className="text-sm text-zinc-300">
                {dragging ? 'Rilascia per selezionare' : 'Trascina qui o clicca per scegliere'}
              </div>
              <div className="text-xs text-zinc-500">PDF / JPG / PNG / WebP / HEIC</div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
            className="hidden"
            onChange={onInputChange}
          />
        </div>

        {upload.isError && (
          <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
            {upload.error instanceof Error ? upload.error.message : String(upload.error)}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={upload.isPending}>
            Annulla
          </Button>
          <Button onClick={() => file && upload.mutate(file)} disabled={!file || upload.isPending}>
            <FileSignature className="size-4" />
            {upload.isPending ? 'Caricamento…' : 'Carica e accetta'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

function signedDocUrl(p: ProposalRow): string | null {
  if (!p.signedDocumentObjectKey) return null
  // signedDocumentObjectKey is "<tenant>/proposals/<slug>/signed/<file>".
  // Strip "<tenant>/proposals/" so we can hit /api/storage/proposals/<slug>/signed/<file>
  const m = p.signedDocumentObjectKey.match(/^[^/]+\/proposals\/(.+)$/)
  if (!m) return null
  return `/api/storage/proposals/${m[1]}`
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

// ============================================================
// Review-signed dialog — admin verifies a pending_review document.
// Shows preview + audit metadata + Approve/Reject actions.
// On reject: deletes the signed doc, status → 'sent', link re-armed.
// On approve: status → 'accepted'.
// ============================================================
function ReviewSignedDialog({
  proposal,
  onClose,
}: {
  proposal: ProposalRow
  onClose: () => void
}) {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [reason, setReason] = useState('')

  const approve = useMutation({
    mutationFn: () => apiClient.post(`/proposals/${proposal.id}/approve-signed`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      onClose()
    },
  })

  const reject = useMutation({
    mutationFn: () =>
      apiClient.post(`/proposals/${proposal.id}/reject-signed`, { reason: reason || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      onClose()
    },
  })

  async function onApprove() {
    const ok = await confirm({
      title: `Approvare la firma di ${proposal.code} R${proposal.revision}?`,
      message:
        'La proposta passerà in stato "Accettata". Hai verificato che il file firmato è valido e corrisponde al committente?',
      confirmLabel: 'Approva',
      tone: 'success',
    })
    if (ok) approve.mutate()
  }

  async function onReject() {
    const ok = await confirm({
      title: `Rifiutare la firma di ${proposal.code} R${proposal.revision}?`,
      message:
        'Il file firmato verrà eliminato da MinIO, la proposta tornerà in stato "Inviata" e il link pubblico sarà nuovamente attivo. L\'azione è registrata in audit.',
      confirmLabel: 'Rifiuta',
      tone: 'danger',
    })
    if (ok) reject.mutate()
  }

  const url = signedDocUrl(proposal)
  const docLabel = proposal.type === 'CTR' ? 'contratto' : 'proposta'

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Verifica firma — ${proposal.code} R${proposal.revision}`}
      size="lg"
    >
      <div className="p-5 space-y-4">
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
          Il cliente ha caricato il documento firmato tramite link pubblico. Verifica che sia
          autentico e completo, poi <strong>Approva</strong> per accettare il {docLabel} o{' '}
          <strong>Rifiuta</strong> per richiedere una nuova firma.
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <Info label="Caricato il">
            {proposal.signedDocumentUploadedAt
              ? new Date(proposal.signedDocumentUploadedAt).toLocaleString('it-IT')
              : '—'}
          </Info>
          <Info label="Origine">
            {proposal.signedDocumentUploadedVia === 'public_link'
              ? 'Link pubblico cliente'
              : proposal.signedDocumentUploadedVia === 'admin'
                ? 'Admin'
                : '—'}
          </Info>
          <Info label="File originale">{proposal.signedDocumentOriginalFilename ?? '—'}</Info>
          <Info label="Dimensione">
            {proposal.signedDocumentSize ? formatBytes(Number(proposal.signedDocumentSize)) : '—'}
          </Info>
          <Info label="IP">{proposal.signedDocumentUploaderIp ?? '—'}</Info>
          <Info label="MIME">{proposal.signedDocumentContentType ?? '—'}</Info>
          {proposal.signedDocumentUploaderUserAgent && (
            <div className="col-span-2 break-all">
              <Info label="User-Agent">{proposal.signedDocumentUploaderUserAgent}</Info>
            </div>
          )}
        </div>

        {url && (
          <iframe
            src={url}
            title="Documento firmato"
            className="w-full h-[50vh] rounded-md border border-zinc-800 bg-white"
          />
        )}

        <div>
          <Label htmlFor="reject-reason">Motivo del rifiuto (audit + cliente, opzionale)</Label>
          <Input
            id="reject-reason"
            placeholder="Es. firma illeggibile, manca firma su pagina 3, ecc."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        {(approve.isError || reject.isError) && (
          <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
            {approve.isError ? String(approve.error) : reject.isError ? String(reject.error) : ''}
          </div>
        )}

        <div className="flex justify-between gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={onReject}
            disabled={reject.isPending || approve.isPending}
            className="text-pink-400 hover:bg-pink-500/10"
          >
            <XCircle className="size-4" />
            {reject.isPending ? 'Rifiuto…' : 'Rifiuta + Elimina file'}
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Chiudi
            </Button>
            <Button onClick={onApprove} disabled={approve.isPending || reject.isPending}>
              <CheckCircle2 className="size-4" />
              {approve.isPending ? 'Approvazione…' : 'Approva → Accetta'}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-zinc-200">{children}</div>
    </div>
  )
}

// ============================================================
// Rejection info dialog — shows full forensic detail for a rejected
// proposal: reason, origin (client via public link / admin), timestamp,
// IP and User-Agent when available.
// ============================================================
function RejectionInfoDialog({
  proposal,
  onClose,
}: {
  proposal: ProposalRow
  onClose: () => void
}) {
  const fromClient = proposal.rejectionVia === 'public_link'
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Dettagli rifiuto — ${proposal.code} R${proposal.revision}`}
      size="md"
    >
      <div className="p-5 space-y-4">
        <div
          className={`rounded-md border px-3 py-2 text-sm ${
            fromClient
              ? 'border-pink-500/40 bg-pink-500/10 text-pink-200'
              : 'border-zinc-700 bg-zinc-900/60 text-zinc-200'
          }`}
        >
          {fromClient ? (
            <>
              <strong>Rifiutata dal cliente</strong> tramite il link pubblico di firma.
            </>
          ) : (
            <>
              <strong>Marcata come rifiutata</strong> manualmente da un amministratore.
            </>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            Motivo del rifiuto
          </div>
          <div className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-sm text-zinc-100 whitespace-pre-wrap">
            {proposal.rejectionReason || '—'}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <Info label="Data e ora">
            {proposal.rejectedAt ? new Date(proposal.rejectedAt).toLocaleString('it-IT') : '—'}
          </Info>
          <Info label="Origine">
            {fromClient
              ? 'Link pubblico cliente'
              : proposal.rejectionVia === 'admin'
                ? 'Admin'
                : '—'}
          </Info>
          {fromClient && (
            <>
              <Info label="IP">{proposal.rejectionIp ?? '—'}</Info>
              <div className="col-span-2 break-all">
                <Info label="User-Agent">{proposal.rejectionUserAgent ?? '—'}</Info>
              </div>
            </>
          )}
        </div>

        <div className="rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2 text-xs text-zinc-400 leading-relaxed">
          Per ripartire crea una nuova revisione di questa proposta dalla voce di menu{' '}
          <strong>Crea nuova revisione</strong>: clonerà il contenuto attuale così potrai modificare
          quanto serve prima di inviare di nuovo.
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={onClose}>Chiudi</Button>
        </div>
      </div>
    </Dialog>
  )
}

// ============================================================
// Admin reject-proposal dialog — marks the whole proposal as rejected
// (status → 'rejected') with a required reason. Distinct from the
// "reject signed document only" flow inside ReviewSignedDialog.
// ============================================================
function RejectProposalDialog({
  proposal,
  onClose,
}: {
  proposal: ProposalRow
  onClose: () => void
}) {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [reason, setReason] = useState('')

  const reject = useMutation({
    mutationFn: () =>
      apiClient.post(`/proposals/${proposal.id}/status`, {
        status: 'rejected',
        rejectionReason: reason.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      onClose()
    },
  })

  const canSubmit = reason.trim().length >= 5 && !reject.isPending

  async function onConfirm() {
    const ok = await confirm({
      title: `Rifiutare ${proposal.code} R${proposal.revision}?`,
      message:
        'La proposta passerà in stato "Rifiutata". Per ripartire dovrai creare una nuova revisione. L\'azione è registrata in audit.',
      confirmLabel: 'Rifiuta',
      tone: 'danger',
    })
    if (ok) reject.mutate()
  }

  const docLabel = proposal.type === 'CTR' ? 'contratto' : 'proposta'

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Rifiuta ${docLabel} — ${proposal.code} R${proposal.revision}`}
      size="md"
    >
      <div className="p-5 space-y-4">
        <div className="rounded-md border border-pink-500/30 bg-pink-500/10 px-3 py-2 text-sm text-pink-200">
          Stai marcando il {docLabel} come <strong>rifiutato</strong>. Il link pubblico (se
          presente) verrà invalidato e per ripartire dovrai creare una nuova revisione.
        </div>

        <div>
          {/* biome-ignore lint/a11y/noLabelWithoutControl: htmlFor binds to the textarea below */}
          <label
            htmlFor="admin-reject-reason"
            className="block text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-1"
          >
            Motivo del rifiuto (richiesto)
          </label>
          <textarea
            id="admin-reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Es. cliente ha rifiutato verbalmente, mancato budget, scope cambiato, ecc."
            rows={5}
            className="w-full rounded-md border border-zinc-700 bg-zinc-950/60 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-cyan-500/60 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 resize-y"
          />
          <p className="mt-1 text-[11px] text-zinc-500">
            Minimo 5 caratteri. Verrà salvato in audit e mostrato nella scheda della proposta.
          </p>
        </div>

        {reject.isError && (
          <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
            {String(reject.error)}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={reject.isPending}>
            Annulla
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!canSubmit}
            className="bg-pink-500 hover:bg-pink-400 text-white"
          >
            <XCircle className="size-4" />
            {reject.isPending ? 'Invio…' : 'Conferma rifiuto'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ============================================================
// Public link info dialog — copy / open the public signing URL,
// or rotate it (generates a fresh token + 30d expiry).
// ============================================================
function PublicLinkDialog({
  proposal,
  onClose,
}: {
  proposal: ProposalRow
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [copied, setCopied] = useState(false)
  const [currentToken, setCurrentToken] = useState(proposal.publicLinkToken)
  const [currentExpiresAt, setCurrentExpiresAt] = useState(proposal.publicLinkExpiresAt)

  const url = currentToken ? `${window.location.origin}/p/${currentToken}` : ''

  const regenerate = useMutation({
    mutationFn: () =>
      apiClient.post<{ publicLinkToken: string; publicLinkExpiresAt: string | null }>(
        `/proposals/${proposal.id}/regenerate-public-link`,
        {},
      ),
    onSuccess: (r) => {
      setCurrentToken(r.publicLinkToken)
      setCurrentExpiresAt(r.publicLinkExpiresAt)
      qc.invalidateQueries({ queryKey: ['proposals'] })
    },
  })

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }

  const docLabel = proposal.type === 'CTR' ? 'contratto' : 'proposta'

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Link pubblico di firma — ${proposal.code} R${proposal.revision}`}
      size="md"
    >
      <div className="p-5 space-y-4">
        <p className="text-sm text-zinc-400 leading-relaxed">
          Invia questo link al cliente per la firma del {docLabel}. Il link permette di vedere
          l&apos;anteprima, stamparla, e caricare il documento firmato.{' '}
          <strong>Validità 30 giorni</strong>; viene consumato al primo upload.
        </p>

        <div className="space-y-2">
          <Label htmlFor="public-url">URL pubblico</Label>
          <div className="flex gap-2">
            <Input
              id="public-url"
              readOnly
              value={url}
              onFocus={(e) => e.currentTarget.select()}
              className="font-mono text-xs"
            />
            <Button variant="secondary" size="sm" onClick={copyToClipboard}>
              {copied ? <CheckCircle2 className="size-4" /> : <LinkIcon className="size-4" />}
              {copied ? 'Copiato' : 'Copia'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => window.open(url, '_blank')}>
              <ExternalLink className="size-4" /> Apri
            </Button>
          </div>
        </div>

        <div className="text-xs text-zinc-500 space-y-0.5">
          <div>
            Scade:{' '}
            <span className="text-zinc-300">
              {currentExpiresAt ? new Date(currentExpiresAt).toLocaleString('it-IT') : '—'}
            </span>
          </div>
          {proposal.publicLinkUsedAt && (
            <div className="text-amber-300">
              Già utilizzato il {new Date(proposal.publicLinkUsedAt).toLocaleString('it-IT')} — il
              link corrente non è più valido finché non lo rigeneri.
            </div>
          )}
        </div>

        {regenerate.isError && (
          <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
            {String(regenerate.error)}
          </div>
        )}

        <div className="flex justify-between gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending}
          >
            <RefreshCw className={`size-4 ${regenerate.isPending ? 'animate-spin' : ''}`} />
            {regenerate.isPending ? 'Rigenero…' : 'Rigenera link'}
          </Button>
          <Button onClick={onClose}>Chiudi</Button>
        </div>
      </div>
    </Dialog>
  )
}

// ============================================================
// Revise dialog
// ============================================================
function ReviseDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [note, setNote] = useState('')

  const revise = useMutation({
    mutationFn: () => apiClient.post(`/proposals/${id}/revise`, { note: note || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      onClose()
    },
  })

  return (
    <Dialog open onClose={onClose} title="Crea revisione" size="sm">
      <div className="p-5 space-y-4">
        <p className="text-sm text-zinc-400 leading-relaxed">
          Crea una nuova revisione (R+1) clonando il contenuto attuale. La revisione precedente
          passerà allo stato <span className="text-zinc-300">superseded</span> e potrai modificare
          la nuova in bozza.
        </p>
        <div>
          <Label htmlFor="revise-note">Motivo (audit)</Label>
          <Input
            id="revise-note"
            placeholder="Es. modifiche pagamento da richiesta cliente"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            autoFocus
          />
        </div>
        {revise.isError && (
          <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
            Errore: {String(revise.error)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={() => revise.mutate()} disabled={revise.isPending}>
            <GitBranch className="size-4" />
            {revise.isPending ? 'Creazione…' : 'Crea revisione'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ============================================================
// Empty content shell for new proposals
// ============================================================
function emptyContent(clientName: string) {
  const today = new Date().toISOString().slice(0, 10)
  return {
    schemaVersion: '1.0',
    language: 'it',
    header: {
      title: 'Proposta Tecnico-Commerciale',
      subtitle: '',
      issuedOn: today,
    },
    client: {
      clientId: null,
      ragioneSociale: clientName,
      indirizzo: '',
    },
    executiveSummary: '',
    context: {
      obiettivoBox: '',
      descrizioneBusiness: '',
      attivitaPreviste: [],
    },
    sections: [],
    pricing: {
      voci: [],
      totaleProgettoEur: 0,
      vatRate: 22,
    },
    payment: {
      modello: 'percent',
      perConfigurazione: [
        {
          configKey: 'unica',
          tranches: [
            { label: 'Anticipo alla firma', percentuale: 30 },
            { label: 'Saldo a consegna', percentuale: 70 },
          ],
        },
      ],
      scadenza: '30 giorni data fattura',
    },
    conditions: {
      validitaGiorni: 30,
      trasferte:
        'Costi vivi (km, pedaggi, pasti, pernottamenti) esclusi e fatturati a parte secondo tabelle ACI.',
    },
    acceptance: {
      firmaCliente: {
        ragioneSociale: clientName,
      },
      firmaFornitore: {
        ragioneSociale: 'G soLutions di Luzu Gabriele',
      },
    },
  }
}

// Re-export for tests / external use
export { emptyContent }

// Stub used by Suspense / placeholders elsewhere
export type { ProposalRow }

// ============================================================
// Empty content shell for CTR contracts
// ============================================================
function emptyContractContent(clientName: string) {
  const today = new Date().toISOString().slice(0, 10)
  return {
    schemaVersion: '1.0',
    language: 'it',
    header: {
      title: 'Contratto di Fornitura Software',
      subtitle: "Sviluppo, Licenza d'Uso e Manutenzione",
      issuedOn: today,
      luogoStipula: 'Formigine (MO)',
    },
    premesse: {
      riferimentiNormativi: [
        'artt. 2222 e seguenti del Codice Civile',
        'GDPR Reg. UE 2016/679',
        "Legge 22 aprile 1941, n. 633 (diritto d'autore)",
      ],
      consideratoChe: [
        'il Fornitore svolge professionalmente attività di progettazione, sviluppo e manutenzione di software personalizzato',
        'il Committente intende avvalersi dei servizi del Fornitore per lo sviluppo del software descritto in Allegato A',
      ],
      sopraEsposto: 'Tutto ciò premesso, le Parti convengono e stipulano quanto segue:',
    },
    parties: {
      fornitore: {
        ragioneSociale: 'G soLutions di Luzu Gabriele',
        sedeLegale: {
          indirizzo: 'Via Cherubini 3',
          cap: '41043',
          citta: 'Formigine',
          provincia: 'MO',
          paese: 'Italia',
        },
        partitaIva: '04227230366',
        codiceFiscale: 'LZUGRL91E28I462U',
        legaleRappresentante: 'Gabriele Luzu',
        qualifica: 'Titolare',
        email: 'info@gsolutions-it.com',
        ruoloContratto: 'Fornitore',
      },
      committente: {
        ragioneSociale: clientName,
        sedeLegale: { indirizzo: '', paese: 'Italia' },
        partitaIva: '',
        legaleRappresentante: '',
        ruoloContratto: 'Committente',
      },
    },
    oggetto: {
      nomeSoftware: '',
      descrizione: '',
      moduliInclusi: [],
      moduliEsclusi: [],
      allegatoARef: '',
    },
    corrispettivi: {
      importoCifre: 0,
      importoLettere: '',
      vatRate: 22,
      tranches: [
        { label: 'Acconto alla firma', percentuale: 30 },
        { label: 'Saldo a consegna', percentuale: 70 },
      ],
      coordinate: {
        intestatario: 'Gabriele Luzu',
        iban: 'IT48 V036 6901 6003 0713 3039 418',
        istituto: 'Revolut Bank UAB',
        indirizzoBanca: 'Via Dante 7, 20123 Milano (MI)',
      },
      interesseMoraTassoAnnuo: 8,
    },
    tempistiche: {
      durataIndicativaSettimane: 8,
      riferimentoAllegatoA: true,
      decorrenzaDa: 'anticipo',
    },
    collaudo: { giorniCollaudo: 15, maxCicliRevisione: 3, accettazioneTacitaGiorni: 15 },
    durataRecesso: {
      efficaciaDa: 'sottoscrizione',
      recessoCommittentePenalePercResiduo: 20,
      risoluzioneFornitoreCausaGravissimo: true,
    },
    obblighi: {
      fornitore: [
        'operare con la diligenza professionale ex art. 1176, comma 2 c.c.',
        'mantenere la riservatezza su dati e informazioni del Committente',
        'garantire autonomia organizzativa e gestionale',
      ],
      committente: [
        'fornire al Fornitore tutti i dati, accessi e specifiche necessari',
        'collaborare attivamente nelle fasi di analisi e collaudo',
        'effettuare i pagamenti secondo le scadenze pattuite',
      ],
    },
    proprietaIntellettuale: {
      cessioneCondizionataASaldo: true,
      consegnaCodiceSuRichiesta: true,
      riservaKnowHowFornitore: true,
    },
    riservatezza: { durataAnniPostCessazione: 2 },
    garanziaManutenzione: {
      garanziaMesi: 12,
      garanziaInclude: ['Correzione gratuita di bug rispetto alle specifiche Allegato A'],
      garanziaEsclude: [
        'utilizzo improprio del Software',
        'modifiche al codice da parte di terzi non autorizzati',
        'malfunzionamenti dovuti a fattori esterni (rete, hardware, OS)',
      ],
      manutenzioneEvolutiva: { tariffaOraria: 65 },
    },
    gdpr: { nominaResponsabileTrattamento: true },
    responsabilita: {
      soloDanniDirettiColpaGraveDolo: true,
      capAlCorrispettivoTotale: true,
      esclusi: ['danni indiretti', 'lucro cessante', 'perdita di dati'],
    },
    forzaMaggiore: { giorniMaxPrimaRecesso: 90 },
    comunicazioni: {
      fornitoreEmail: 'info@gsolutions-it.com',
      committenteEmail: '',
      pecValida: true,
      emailValida: true,
    },
    disposizioniFinali: {
      interoAccordo: true,
      modificheSoloScritte: true,
      invaliditaParziale: true,
      allegati: [],
    },
    leggeForo: {
      leggeApplicabile: 'italiana',
      foro: 'Modena',
      mediazioneObbligatoria: true,
      durataMediazione: { giorni: 30 },
    },
    clausoleVessatorie: { articoliApprovatiNumeri: [3, 5, 6, 9, 11, 13, 14, 17] },
    articoliExtra: [],
    firme: {
      luogoData: '',
      committente: { ragioneSociale: clientName, legaleRappresentante: '' },
      fornitore: {
        ragioneSociale: 'G soLutions di Luzu Gabriele',
        legaleRappresentante: 'Gabriele Luzu',
        qualifica: 'Titolare',
      },
    },
  }
}

export { emptyContractContent }
